/**
 * chat-attachments 버킷 URL ↔ 객체 경로.
 *
 * locarora-admin `lib/chat/chat-attachment-url.ts` 와 **같은 내용**을 유지한다.
 *
 * 버킷이 private 으로 바뀌면 `messages.attachments[].url` 에 저장된 공개 URL
 * (`/storage/v1/object/public/chat-attachments/{roomId}/...`) 은 더 이상 열리지 않는다.
 * DB 값은 그대로 두고, 표시 직전에 이 경로로 서명 URL 을 만든다.
 *
 * - 공개형(`/object/public/chat-attachments/`)·서명형(`/object/sign/chat-attachments/`)
 *   URL 만 인정한다. product_card 이미지(product-images 버킷)·외부 URL·blob: 은 null.
 * - 객체 키 형식은 `{chatRoomId}/{...}` — 첫 세그먼트가 UUID 가 아니거나 `..`/빈
 *   세그먼트가 섞여 있으면 null (서명 요청에 이상한 경로를 넘기지 않는다).
 */

export const CHAT_ATTACHMENTS_BUCKET = "chat-attachments";

const URL_MARKERS = [
  `/storage/v1/object/public/${CHAT_ATTACHMENTS_BUCKET}/`,
  `/storage/v1/object/sign/${CHAT_ATTACHMENTS_BUCKET}/`,
];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** chat-attachments 버킷 URL 이면 객체 경로, 아니면 null. */
export function chatAttachmentPath(url: unknown): string | null {
  if (typeof url !== "string" || url.length === 0) return null;

  let raw: string | null = null;
  for (const marker of URL_MARKERS) {
    const idx = url.indexOf(marker);
    if (idx !== -1) {
      raw = url.slice(idx + marker.length);
      break;
    }
  }
  if (raw === null) return null;

  raw = raw.split(/[?#]/)[0];
  let path: string;
  try {
    path = decodeURIComponent(raw);
  } catch {
    path = raw;
  }

  const segments = path.split("/");
  if (segments.length < 2) return null;
  if (!UUID_RE.test(segments[0])) return null;
  if (segments.some((s) => s === "" || s === "." || s === "..")) return null;
  return path;
}

/** 서명 대상 첨부(type image|file) 인지. product_card 등은 제외. */
export function isSignableChatAttachment(
  attachment: unknown,
): attachment is { url: string; type: "image" | "file"; name?: string } {
  if (!attachment || typeof attachment !== "object") return false;
  const a = attachment as { url?: unknown; type?: unknown };
  return (
    (a.type === "image" || a.type === "file") &&
    chatAttachmentPath(a.url) !== null
  );
}

/** 메시지 목록의 attachments 에서 서명이 필요한 객체 경로(중복 제거). */
export function collectChatAttachmentPaths(
  messages: ReadonlyArray<{ attachments?: unknown } | null | undefined>,
): string[] {
  const out = new Set<string>();
  for (const m of messages) {
    const list = m?.attachments;
    if (!Array.isArray(list)) continue;
    for (const a of list) {
      if (!isSignableChatAttachment(a)) continue;
      const path = chatAttachmentPath(a.url);
      if (path) out.add(path);
    }
  }
  return [...out];
}
